/**
 * Visual timeline component for patient appointments
 * ZUB appointments on left, timeline in middle, DH on right
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar as CalendarIcon } from 'lucide-react'
import { formatDateCzech } from '@/lib/calendar-utils'
import { STATUS_BACKGROUNDS, isCancelledBackground } from '@/lib/constants'
import type { PatientAppointment } from '@/types/patient'

interface PatientVisualTimelineProps {
	appointments: PatientAppointment[]
}

interface TimelineEntry {
	date: string
	zub: PatientAppointment[]
	dh: PatientAppointment[]
}

interface YearGroup {
	year: string
	entries: TimelineEntry[]
}

export function PatientVisualTimeline({ appointments }: PatientVisualTimelineProps) {
	const navigate = useNavigate()

	// Group appointments by date, then by year
	const yearGroups = useMemo(() => {
		const entriesMap = new Map<string, TimelineEntry>()

		// Sort appointments by date descending (newest first)
		const sorted = [...appointments].sort(
			(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
		)

		sorted.forEach((apt) => {
			const existing = entriesMap.get(apt.date) || {
				date: apt.date,
				zub: [],
				dh: [],
			}

			if (apt.type === 'ZUB') {
				existing.zub.push(apt)
			} else if (apt.type === 'DH') {
				existing.dh.push(apt)
			}

			entriesMap.set(apt.date, existing)
		})

		// Group entries by year
		const yearMap = new Map<string, YearGroup>()
		Array.from(entriesMap.values()).forEach((entry) => {
			const year = entry.date.substring(0, 4)
			const existing = yearMap.get(year) || { year, entries: [] }
			existing.entries.push(entry)
			yearMap.set(year, existing)
		})

		return Array.from(yearMap.values())
	}, [appointments])

	const handleGoToAppointment = (apt: PatientAppointment) => {
		const type = apt.type.toLowerCase()
		navigate(`/calendar/${type}?date=${apt.date}`)
	}

	if (appointments.length === 0) {
		return (
			<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
				<h2 className="mb-4 text-lg font-semibold text-[#3d3428]">Časová osa</h2>
				<div className="flex flex-col items-center justify-center py-12 text-[#8c8275]">
					<div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#f5ebe0] mb-4">
						<CalendarIcon className="h-8 w-8 text-[#d7ad6c] opacity-60" />
					</div>
					<p className="text-[#a09689]">Žádné termíny v historii</p>
				</div>
			</div>
		)
	}

	return (
		<div className="rounded-xl bg-white shadow-sm border border-[#e8e4df] overflow-hidden">
			{/* Header with type labels */}
			<div className="sticky top-0 z-20 bg-white border-b border-[#e8e4df] p-4">
				<div className="grid grid-cols-[1fr_80px_1fr] gap-4">
					<div className="text-center">
						<span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#f5ebe0] text-[#a88544] font-medium">
							<span className="w-2 h-2 rounded-full bg-[#d7ad6c]" />
							ZUB
						</span>
					</div>
					<div className="text-center">
						<h2 className="text-lg font-semibold text-[#3d3428]">Časová osa</h2>
					</div>
					<div className="text-center">
						<span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-700 font-medium">
							<span className="w-2 h-2 rounded-full bg-blue-500" />
							DH
						</span>
					</div>
				</div>
			</div>

			{/* Timeline content */}
			<div className="p-6">
				{yearGroups.map((yearGroup) => (
					<YearSection
						key={yearGroup.year}
						yearGroup={yearGroup}
						onGoTo={handleGoToAppointment}
					/>
				))}
			</div>
		</div>
	)
}

interface YearSectionProps {
	yearGroup: YearGroup
	onGoTo: (apt: PatientAppointment) => void
}

function YearSection({ yearGroup, onGoTo }: YearSectionProps) {
	return (
		<div className="relative">
			{/* Sticky year header */}
			<div className="sticky top-[73px] z-10 py-3 -mx-6 px-6 bg-white/95 backdrop-blur-sm border-b border-[#f0ede8]">
				<div className="flex justify-center">
					<span className="inline-flex items-center px-6 py-2 rounded-full bg-gradient-to-r from-[#f5ebe0] to-[#e8dfd0] border border-[#d7ad6c] text-[#3d3428] font-bold text-xl shadow-sm">
						{yearGroup.year}
					</span>
				</div>
			</div>

			{/* Timeline entries for this year */}
			<div className="relative mt-6 space-y-10">
				{yearGroup.entries.map((entry, index) => (
					<TimelineRow
						key={entry.date}
						entry={entry}
						isLast={index === yearGroup.entries.length - 1}
						onGoTo={onGoTo}
					/>
				))}
			</div>
		</div>
	)
}

interface TimelineRowProps {
	entry: TimelineEntry
	isLast: boolean
	onGoTo: (apt: PatientAppointment) => void
}

function TimelineRow({ entry, isLast, onGoTo }: TimelineRowProps) {
	return (
		<div className="grid grid-cols-[1fr_80px_1fr] gap-4">
			{/* ZUB column (left) */}
			<div className="flex flex-col gap-2 justify-center">
				{entry.zub.length > 0 ? (
					entry.zub.map((apt) => (
						<AppointmentCard key={apt.id} appointment={apt} side="left" onGoTo={() => onGoTo(apt)} />
					))
				) : (
					<div className="h-16" /> // Placeholder for alignment
				)}
			</div>

			{/* Center timeline */}
			<div className="relative flex flex-col items-center justify-center">
				{/* Date circle */}
				<div className="z-10 flex flex-col items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#f5ebe0] to-[#e8dfd0] border-2 border-[#d7ad6c] shadow-sm">
					<span className="text-lg font-bold text-[#3d3428]">
						{formatDateCzech(new Date(entry.date), 'd')}
					</span>
					<span className="text-[10px] text-[#8c8275] uppercase">
						{formatDateCzech(new Date(entry.date), 'MMM')}
					</span>
				</div>

				{/* Connecting line to next entry */}
				{!isLast && (
					<div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-10 bg-gradient-to-b from-[#d7ad6c] to-[#e8e4df]" />
				)}
			</div>

			{/* DH column (right) */}
			<div className="flex flex-col gap-2 justify-center">
				{entry.dh.length > 0 ? (
					entry.dh.map((apt) => (
						<AppointmentCard key={apt.id} appointment={apt} side="right" onGoTo={() => onGoTo(apt)} />
					))
				) : (
					<div className="h-16" /> // Placeholder for alignment
				)}
			</div>
		</div>
	)
}

interface AppointmentCardProps {
	appointment: PatientAppointment
	side: 'left' | 'right'
	onGoTo: () => void
}

function AppointmentCard({ appointment, side, onGoTo }: AppointmentCardProps) {
	const bg = appointment.background
	const isNoShow = bg === STATUS_BACKGROUNDS.NO_SHOW || bg === '#d50000'
	const isCancelled =
		bg === STATUS_BACKGROUNDS.CANCELLED || bg === '#616161' || isCancelledBackground(bg)

	const isZub = appointment.type === 'ZUB'
	const cardBg = isZub ? 'bg-[#fdf8f0]' : 'bg-blue-50/50'
	const borderColor = isZub ? 'border-[#e5c896]' : 'border-blue-100'
	const hoverBorder = isZub ? 'hover:border-[#d7ad6c]' : 'hover:border-blue-300'

	return (
		<button
			type="button"
			onClick={onGoTo}
			className={`group relative w-full max-w-full rounded-xl border ${borderColor} ${cardBg} p-3 transition-all ${hoverBorder} hover:shadow-md cursor-pointer text-left ${
				isCancelled ? 'opacity-60' : ''
			}`}
		>
			{/* Connector line to timeline */}
			<div
				className={`absolute top-1/2 -translate-y-1/2 w-4 h-0.5 ${
					isZub ? 'bg-[#d7ad6c]' : 'bg-blue-400'
				} ${side === 'left' ? 'left-full' : 'right-full'}`}
			/>

			{/* Color indicator */}
			<div
				className={`absolute top-3 ${side === 'left' ? 'left-3' : 'right-3'} w-2 h-2 rounded-full`}
				style={{ backgroundColor: bg || (isZub ? '#d7ad6c' : '#3b82f6') }}
			/>

			{/* Time */}
			<p className={`text-xs font-medium ${isZub ? 'text-[#a88544]' : 'text-blue-600'} mb-1 ${side === 'left' ? 'pl-4' : 'pr-4'}`}>
				{appointment.startTime} - {appointment.endTime}
			</p>

			{/* Title */}
			<p
				className={`text-sm font-medium ${side === 'left' ? 'pl-4' : 'pr-4'} ${
					isCancelled ? 'text-[#a09689]' : 'text-[#3d3428]'
				}`}
			>
				{appointment.title || 'Bez názvu'}
			</p>

			{/* Note preview */}
			{appointment.note && (
				<p className={`text-xs text-[#8c8275] mt-1 ${side === 'left' ? 'pl-4' : 'pr-4'}`}>
					{appointment.note}
				</p>
			)}

			{/* Status badge */}
			{(isNoShow || isCancelled) && (
				<div className={`mt-2 ${side === 'left' ? 'pl-4' : 'pr-4'}`}>
					{isNoShow && (
						<span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
							Nepřišel
						</span>
					)}
					{isCancelled && !isNoShow && (
						<span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
							Zrušeno
						</span>
					)}
				</div>
			)}
		</button>
	)
}
